import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import "./App.css";
import { AuthProvider, useAuth } from "./lib/auth";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import { CustomersList, CustomerForm, CustomerDetail } from "./pages/Customers";
import PriceBook from "./pages/PriceBook";
import { EstimatesList, EstimateForm } from "./pages/Estimates";
import { InvoicesList, InvoiceForm } from "./pages/Invoices";
import InvoiceRedirect from "./pages/InvoiceRedirect";
import Settings from "./pages/Settings";
import SettingsInvoices from "./pages/SettingsInvoices";
import SettingsCompany from "./pages/settings/SettingsCompany";
import SettingsNotifications from "./pages/settings/SettingsNotifications";
import SettingsEstimates from "./pages/settings/SettingsEstimates";
import SettingsJobs from "./pages/settings/SettingsJobs";
import SettingsAccount from "./pages/settings/SettingsAccount";
import SettingsApiKeys from "./pages/settings/SettingsApiKeys";
import Schedule from "./pages/Schedule";
import { JobsList, JobDetail, NewJob } from "./pages/Jobs";
import Timesheets from "./pages/Timesheets";
import Expenses from "./pages/Expenses";
import Mileage from "./pages/Mileage";
import Employees from "./pages/Employees";
import Reports from "./pages/Reports";
import EmailSettings from "./pages/EmailSettings";
import RecurringInvoices from "./pages/RecurringInvoices";
import AuditLog from "./pages/AuditLog";
import MyMoney from "./pages/MyMoney";
// Customer Portal
import { BrandingProvider } from "./portal/branding";
import PortalLayout from "./portal/PortalLayout";
import PortalLogin from "./portal/PortalLogin";
import PortalAuth from "./portal/PortalAuth";
import PortalHome from "./portal/PortalHome";
import PortalEstimateDetail from "./portal/PortalEstimateDetail";
import PortalInvoiceDetail from "./portal/PortalInvoiceDetail";
import { getPortalToken } from "./portal/portalApi";
// Public tokenized (no-auth) views
import PublicInvoiceView from "./pages/public/PublicInvoiceView";
import PublicEstimateView from "./pages/public/PublicEstimateView";
import SendInvoicePreview from "./pages/SendInvoicePreview";

function Private({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <div className="min-h-screen flex items-center justify-center text-sm">Access denied — admin only.</div>;
  return <Layout>{children}</Layout>;
}

function PrivateFullscreen({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Loading...</div>;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function ThemeInit() {
  useEffect(() => {
    if (localStorage.getItem("theme") === "dark") document.documentElement.classList.add("dark");
  }, []);
  return null;
}

function PortalPrivate({ children }) {
  if (!getPortalToken()) return <Navigate to="/portal/login" replace />;
  return <PortalLayout>{children}</PortalLayout>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public tokenized invoice/estimate views (no auth) */}
      <Route path="/i/:token" element={<PublicInvoiceView />} />
      <Route path="/e/:token" element={<PublicEstimateView />} />

      {/* Customer Portal (separate branded experience) */}
      <Route path="/portal/login" element={<BrandingProvider><PortalLayout><PortalLogin /></PortalLayout></BrandingProvider>} />
      <Route path="/portal/auth" element={<BrandingProvider><PortalLayout><PortalAuth /></PortalLayout></BrandingProvider>} />
      <Route path="/portal" element={<BrandingProvider><PortalPrivate><PortalHome /></PortalPrivate></BrandingProvider>} />
      <Route path="/portal/estimates/:id" element={<BrandingProvider><PortalPrivate><PortalEstimateDetail /></PortalPrivate></BrandingProvider>} />
      <Route path="/portal/invoices/:id" element={<BrandingProvider><PortalPrivate><PortalInvoiceDetail /></PortalPrivate></BrandingProvider>} />

      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<Private><Dashboard /></Private>} />
      <Route path="/dashboard-v3" element={<Private><Dashboard /></Private>} />
      <Route path="/my-money" element={<Private><MyMoney /></Private>} />
      <Route path="/emails" element={<Private><Navigate to="/email-settings" replace /></Private>} />
      <Route path="/marketing" element={<Private><EmailSettings /></Private>} />
      <Route path="/schedule" element={<Private><Schedule /></Private>} />
      <Route path="/jobs" element={<Private><JobsList /></Private>} />
      <Route path="/jobs/new" element={<Private><NewJob /></Private>} />
      <Route path="/jobs/:id" element={<Private><JobDetail /></Private>} />
      <Route path="/customers" element={<Private><CustomersList /></Private>} />
      <Route path="/customers/new" element={<Private><CustomerForm /></Private>} />
      <Route path="/customers/:id" element={<Private><CustomerDetail /></Private>} />
      <Route path="/customers/:id/edit" element={<Private><CustomerForm /></Private>} />
      <Route path="/timesheets" element={<Private><Timesheets /></Private>} />
      <Route path="/expenses" element={<Private><Expenses /></Private>} />
      <Route path="/mileage" element={<Private><Mileage /></Private>} />
      <Route path="/pricebook" element={<Private><PriceBook /></Private>} />
      <Route path="/estimates" element={<Private><EstimatesList /></Private>} />
      <Route path="/estimates/new" element={<Private><EstimateForm /></Private>} />
      <Route path="/estimates/:id" element={<Private><EstimateForm /></Private>} />
      <Route path="/invoices" element={<Private><InvoicesList /></Private>} />
      <Route path="/invoices/new" element={<Private><InvoiceForm /></Private>} />
      <Route path="/invoices/:id/send" element={<PrivateFullscreen><SendInvoicePreview /></PrivateFullscreen>} />
      <Route path="/invoices/:id" element={<Private><InvoiceRedirect /></Private>} />
      <Route path="/recurring" element={<Private><RecurringInvoices /></Private>} />
      <Route path="/reports" element={<Private><Reports /></Private>} />
      <Route path="/employees" element={<Private><Employees /></Private>} />
      <Route path="/email-settings" element={<Private roles={["admin"]}><EmailSettings /></Private>} />
      <Route path="/audit" element={<Private roles={["admin"]}><AuditLog /></Private>} />
      <Route path="/settings" element={<Private><Settings /></Private>} />
      <Route path="/settings/invoices" element={<Private><SettingsInvoices /></Private>} />
      <Route path="/settings/company" element={<Private><SettingsCompany /></Private>} />
      <Route path="/settings/notifications" element={<Private><SettingsNotifications /></Private>} />
      <Route path="/settings/estimates" element={<Private><SettingsEstimates /></Private>} />
      <Route path="/settings/jobs" element={<Private><SettingsJobs /></Private>} />
      <Route path="/settings/account" element={<Private><SettingsAccount /></Private>} />
      <Route path="/settings/api-keys" element={<Private roles={["admin"]}><SettingsApiKeys /></Private>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ThemeInit />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
